import { useEffect, useState } from "react";
import { useMutation, useQuery } from "@tanstack/react-query";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import { useToast } from "@/hooks/use-toast";
import { queryClient } from "@/lib/queryClient";
import { BellRing, Eye, Loader2, Save, Send, Zap } from "lucide-react";
import {
  WEEKDAYS, errorMessage, getSettings, hourLabel, metricsUrls, previewDigest, runAlerts, sendDigest,
  updateSettings, type MetricsSettings, type SettingsResponse,
} from "@/lib/metrics";
import { ErrorState, TableSkeleton } from "./shared";

const HOURS = Array.from({ length: 24 }, (_, i) => i);

function numberOr(value: string, fallback: number): number {
  const n = Number(value);
  return value.trim() === "" || Number.isNaN(n) ? fallback : n;
}

export default function MetricsSettingsTab() {
  const { toast } = useToast();
  const [form, setForm] = useState<MetricsSettings | null>(null);
  const [emailsText, setEmailsText] = useState("");
  const [preview, setPreview] = useState<string | null>(null);
  const [sendOpen, setSendOpen] = useState(false);

  const { data, isLoading, error } = useQuery<SettingsResponse>({
    queryKey: [metricsUrls.settings()],
    queryFn: getSettings,
  });

  useEffect(() => {
    if (!data?.settings) return;
    setForm(data.settings);
    setEmailsText((data.settings.digest.emails || []).join("\n"));
  }, [data]);

  const saveMutation = useMutation({
    mutationFn: (body: Partial<MetricsSettings>) => updateSettings(body),
    onSuccess: (res) => {
      toast({ title: "Settings saved" });
      queryClient.setQueryData([metricsUrls.settings()], res);
      setForm(res.settings);
      setEmailsText((res.settings.digest.emails || []).join("\n"));
    },
    onError: (e) => toast({ title: "Could not save", description: errorMessage(e), variant: "destructive" }),
  });

  const previewMutation = useMutation({
    mutationFn: previewDigest,
    onSuccess: (res) => setPreview(res.html),
    onError: (e) => toast({ title: "Could not build the preview", description: errorMessage(e), variant: "destructive" }),
  });

  const sendMutation = useMutation({
    mutationFn: () => sendDigest(),
    onSuccess: (res) => {
      toast({
        title: "Digest sent",
        description: res.sent?.length ? `Sent to ${res.sent.join(", ")}.` : "Nobody was on the list, so nothing went out.",
      });
      setSendOpen(false);
    },
    onError: (e) => toast({ title: "Could not send the digest", description: errorMessage(e), variant: "destructive" }),
  });

  const alertsMutation = useMutation({
    mutationFn: runAlerts,
    onSuccess: (res) => {
      toast({
        title: res.fired?.length ? `${res.fired.length} alert${res.fired.length === 1 ? "" : "s"} fired` : "Nothing fired",
        description: res.fired?.length ? res.fired.join(" · ") : "Every check came back inside its threshold.",
      });
    },
    onError: (e) => toast({ title: "Could not run the alerts", description: errorMessage(e), variant: "destructive" }),
  });

  if (isLoading || !form) {
    return <Card><CardContent className="pt-6">{error ? <ErrorState error={error} /> : <TableSkeleton rows={6} cols={3} />}</CardContent></Card>;
  }

  const emails = emailsText.split(/[\n,]/).map((s) => s.trim()).filter(Boolean);
  const save = () => saveMutation.mutate({ ...form, digest: { ...form.digest, emails } });

  return (
    <div className="space-y-6" data-testid="tab-metrics-settings">
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Weekly digest</CardTitle>
          <CardDescription>One email a week with MRR, movements, churn and what needs attention. Times are Jamaica time.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div>
              <Label htmlFor="digest-enabled" className="cursor-pointer">Send the weekly digest</Label>
              <p className="text-xs text-muted-foreground">Turn this off and nothing is sent, though you can still send one by hand below.</p>
            </div>
            <Switch
              id="digest-enabled"
              checked={form.digest.enabled}
              onCheckedChange={(v) => setForm({ ...form, digest: { ...form.digest, enabled: v } })}
              data-testid="switch-digest-enabled"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-2">
            <div>
              <Label className="text-xs">Day</Label>
              <Select value={String(form.digest.weekday)} onValueChange={(v) => setForm({ ...form, digest: { ...form.digest, weekday: Number(v) } })}>
                <SelectTrigger data-testid="select-digest-weekday"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {WEEKDAYS.map((d, i) => <SelectItem key={d} value={String(i)}>{d}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Hour (Jamaica)</Label>
              <Select value={String(form.digest.hour)} onValueChange={(v) => setForm({ ...form, digest: { ...form.digest, hour: Number(v) } })}>
                <SelectTrigger data-testid="select-digest-hour"><SelectValue /></SelectTrigger>
                <SelectContent className="max-h-72">
                  {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>

          <div>
            <Label className="text-xs">Who gets it</Label>
            <Textarea
              value={emailsText}
              onChange={(e) => setEmailsText(e.target.value)}
              rows={3}
              placeholder={"gordon@edufocal.com\nfinance@edufocal.com"}
              data-testid="input-digest-emails"
            />
            <p className="text-xs text-muted-foreground mt-1">One address per line. {emails.length} recipient{emails.length === 1 ? "" : "s"}.</p>
          </div>

          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" onClick={() => previewMutation.mutate()} disabled={previewMutation.isPending} data-testid="button-preview-digest">
              {previewMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Eye className="w-4 h-4 mr-1" />}Preview digest
            </Button>
            <Button size="sm" variant="outline" onClick={() => setSendOpen(true)} data-testid="button-send-digest">
              <Send className="w-4 h-4 mr-1" />Send digest now
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Daily Telegram</CardTitle>
          <CardDescription>A short daily line in Telegram: yesterday's cash, new customers, cancellations and failures.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div>
              <Label htmlFor="telegram-enabled" className="cursor-pointer">Send the daily message</Label>
              <p className="text-xs text-muted-foreground">Goes to the same Telegram chat as the other EduFocal bots.</p>
            </div>
            <Switch
              id="telegram-enabled"
              checked={form.telegramDaily.enabled}
              onCheckedChange={(v) => setForm({ ...form, telegramDaily: { ...form.telegramDaily, enabled: v } })}
              data-testid="switch-telegram-enabled"
            />
          </div>
          <div className="sm:w-1/2">
            <Label className="text-xs">Hour (Jamaica)</Label>
            <Select value={String(form.telegramDaily.hour)} onValueChange={(v) => setForm({ ...form, telegramDaily: { ...form.telegramDaily, hour: Number(v) } })}>
              <SelectTrigger data-testid="select-telegram-hour"><SelectValue /></SelectTrigger>
              <SelectContent className="max-h-72">
                {HOURS.map((h) => <SelectItem key={h} value={String(h)}>{hourLabel(h)}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-base flex items-center gap-2"><BellRing className="w-4 h-4" />Alerts</CardTitle>
          <CardDescription>Checks that run on their own and shout when a number moves further than you expect.</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-start justify-between gap-4 rounded-md border p-3">
            <div>
              <Label htmlFor="alerts-enabled" className="cursor-pointer">Run the alert checks</Label>
              <p className="text-xs text-muted-foreground">When off, nothing is checked and nothing is sent.</p>
            </div>
            <Switch
              id="alerts-enabled"
              checked={form.alerts.enabled}
              onCheckedChange={(v) => setForm({ ...form, alerts: { ...form.alerts, enabled: v } })}
              data-testid="switch-alerts-enabled"
            />
          </div>

          <div className="grid gap-4 sm:grid-cols-3">
            <div>
              <Label className="text-xs">MRR drop %</Label>
              <Input
                value={String(form.alerts.mrrDropPct)}
                onChange={(e) => setForm({ ...form, alerts: { ...form.alerts, mrrDropPct: numberOr(e.target.value, form.alerts.mrrDropPct) } })}
                data-testid="input-mrr-drop"
              />
              <p className="text-xs text-muted-foreground mt-1">Fire when MRR falls this much week on week.</p>
            </div>
            <div>
              <Label className="text-xs">Failed charges multiplier</Label>
              <Input
                value={String(form.alerts.failedChargesMultiplier)}
                onChange={(e) => setForm({ ...form, alerts: { ...form.alerts, failedChargesMultiplier: numberOr(e.target.value, form.alerts.failedChargesMultiplier) } })}
                data-testid="input-failed-multiplier"
              />
              <p className="text-xs text-muted-foreground mt-1">Fire when failures run this many times the usual rate.</p>
            </div>
            <div>
              <Label className="text-xs">Churn spike count</Label>
              <Input
                value={String(form.alerts.churnSpikeCount)}
                onChange={(e) => setForm({ ...form, alerts: { ...form.alerts, churnSpikeCount: numberOr(e.target.value, form.alerts.churnSpikeCount) } })}
                data-testid="input-churn-spike"
              />
              <p className="text-xs text-muted-foreground mt-1">Fire when this many people cancel in a day.</p>
            </div>
          </div>

          <Button size="sm" variant="outline" onClick={() => alertsMutation.mutate()} disabled={alertsMutation.isPending} data-testid="button-run-alerts">
            {alertsMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Zap className="w-4 h-4 mr-1" />}Run alerts now
          </Button>
        </CardContent>
      </Card>

      <div className="flex items-center gap-2">
        <Button onClick={save} disabled={saveMutation.isPending} data-testid="button-save-settings">
          {saveMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Save className="w-4 h-4 mr-1" />}Save settings
        </Button>
        <span className="text-xs text-muted-foreground">Reporting currency is {form.currency}.</span>
      </div>

      <Dialog open={!!preview} onOpenChange={(open) => { if (!open) setPreview(null); }}>
        <DialogContent className="max-w-3xl">
          <DialogHeader>
            <DialogTitle>Digest preview</DialogTitle>
            <DialogDescription>Exactly what would land in the inbox, built from today's numbers.</DialogDescription>
          </DialogHeader>
          <iframe
            title="Digest preview"
            srcDoc={preview || ""}
            className="w-full h-[60vh] rounded-md border bg-white"
            sandbox=""
            data-testid="iframe-digest-preview"
          />
        </DialogContent>
      </Dialog>

      <AlertDialog open={sendOpen} onOpenChange={setSendOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Send the digest now?</AlertDialogTitle>
            <AlertDialogDescription>
              It goes to {emails.length ? emails.join(", ") : "nobody, because the recipient list is empty"} straight away, and to Telegram if that is on.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); sendMutation.mutate(); }}
              disabled={sendMutation.isPending}
              data-testid="button-confirm-send-digest"
            >
              {sendMutation.isPending ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : null}Send now
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
